import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Modal, Segmented, Select, Spin, message } from "antd";
import { AlertTriangle, ArrowRight, RefreshCw, Upload } from "lucide-react";
import axios from "axios";
import { AUTH_TOKEN, DOMAIN_ID, ORG_ID, REACT_APP_API_TENANTS_URL } from "../../../../constants";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../../../design-system/tokens";
import { InlineValue, Note } from "../../../../pages/enduser/ssh-proxy/sshProxyUi";
import {
  addInstance,
  getAdminCredentialStatus,
  listEndpointAccounts,
  listInstances,
  listJumpServer,
  passwordErrorText,
  rerunAdminBootstrap,
  setAdminCredential,
} from "../../../../service/sshProxyService";
import { adminUsernameError, setupPollDelay } from "../adminCredential";
import { gatewayLabel } from "../CaTrustPanel";
import { ScanStatusBody, useScanStatus } from "../setupScan";
import { OnboardShell, PartHead, card } from "./onboardUi";

// Agentless onboarding: a Linux machine the SSH gateway reaches, with nothing
// installed on it by hand. Form (with an existing sudo account) → the gateway
// logs in with that account and sets the machine up → watch the first account
// scan → write a policy.
//
// The address entered here is the one string the whole flow runs on. The
// gateway dials it, the policy names it and the operator types it — and the
// gateway matches hosts exactly, so a display name never stands in for it.
//
// The sudo account's password or key goes straight to adminCredential, which
// vaults it. It lives in the form only until that call returns, and is never
// written to storage.

const SCREENS = ["Endpoint", "Set up the machine", "Verify"];

// An IP or DNS name, no port, no user@. IPv6 is let through as-is; only the
// unambiguous host:port shape is rejected.
const ADDRESS_RE = /^[A-Za-z0-9.:_-]+$/;
const HOST_PORT_RE = /^[^:]+:\d+$/;

// Setup status is read on setupPollDelay's back-off (3 s, then 10 s after a
// minute, 30 s after five), and given up on (with Retry) after this long.
const SETUP_TIMEOUT_MS = 10 * 60 * 1000;

// A key file bigger than this is not a private key.
const MAX_KEY_BYTES = 32 * 1024;

const PASSPHRASE_MSG = "This key is protected by a passphrase. Use one without.";

// Why a pasted private key won't be accepted, or "". The server parses it
// properly; this catches the usual mistakes (a public key, a passphrase) first.
function keyProblem(text) {
  const t = String(text || "").trim();
  if (!t) return "";
  if (!/^-----BEGIN [A-Z0-9 ]*PRIVATE KEY-----/.test(t) || !/-----END [A-Z0-9 ]*PRIVATE KEY-----$/.test(t)) {
    return "Paste an OpenSSH or PEM private key (-----BEGIN … PRIVATE KEY-----).";
  }
  if (/ENCRYPTED/.test(t)) return PASSPHRASE_MSG;
  // An OpenSSH key names its cipher right after the magic; "none" means no
  // passphrase.
  if (t.startsWith("-----BEGIN OPENSSH PRIVATE KEY-----")) {
    try {
      const raw = atob(t.replace(/-----[^-]+-----/g, "").replace(/\s+/g, ""));
      const magic = "openssh-key-v1\0";
      if (raw.startsWith(magic)) {
        const o = magic.length;
        const len = ((raw.charCodeAt(o) << 24) | (raw.charCodeAt(o + 1) << 16) | (raw.charCodeAt(o + 2) << 8) | raw.charCodeAt(o + 3)) >>> 0;
        if (raw.slice(o + 4, o + 4 + len) !== "none") return PASSPHRASE_MSG;
      }
    } catch {
      // Not valid base64: left to the server, which says so.
    }
  }
  return "";
}

// The same call the endpoint page's Description field saves through.
const saveDescription = (instanceId, description) =>
  axios.post(
    `${REACT_APP_API_TENANTS_URL}/api/v1/endpoint/updateDesc`,
    { tenantId: DOMAIN_ID(), orgId: ORG_ID(), endpointId: instanceId, description },
    { headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() } },
  );

function Stepper({ current }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {SCREENS.map((label, i) => {
        const n = i + 1;
        const state = n < current ? "done" : n === current ? "current" : "todo";
        return (
          <div
            key={label}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 999,
              fontFamily: AN_FONT, fontSize: 12.5, fontWeight: state === "current" ? 600 : 500,
              background: state === "current" ? AN_COLORS.primaryL : AN_COLORS.bg,
              color: state === "todo" ? AN_COLORS.muted : state === "done" ? AN_COLORS.success : AN_COLORS.primaryD,
              border: `1px solid ${state === "current" ? AN_COLORS.primary : AN_COLORS.line}`,
            }}
          >
            <span style={{ fontFamily: AN_MONO, fontSize: 11 }}>{n}</span>
            {label}
          </div>
        );
      })}
    </div>
  );
}

export function OnboardSshTarget({ embedded, onDone }) {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [messageApi, msgCtx] = message.useMessage();
  const [modal, modalCtx] = Modal.useModal();
  const method = Form.useWatch("method", form);
  const keyFile = useRef(null);

  const [screen, setScreen] = useState(1);
  const [gateways, setGateways] = useState(null); // null while loading
  const [instances, setInstances] = useState([]);
  const [saving, setSaving] = useState(false);
  // { instanceId, name, address, jumpServerId, gateway, username? } once
  // addInstance succeeds. Kept from then on: a failed or changed credential is
  // re-posted for this endpoint, never by adding it again.
  const [created, setCreated] = useState(null);
  // adminCredential's error, shown on the form.
  const [credError, setCredError] = useState(null);
  const [descSaved, setDescSaved] = useState(false);

  const loadGateways = useCallback(() => {
    setGateways(null);
    listJumpServer({
      payload: { pageSize: 200 },
      onSuccess: (data) => setGateways(data?.jumpServers || []),
      onFailure: () => setGateways([]),
    });
  }, []);

  useEffect(() => {
    loadGateways();
    listInstances({ onSuccess: setInstances, onFailure: () => {} });
  }, [loadGateways]);

  // The setup and the scan carry on server-side whenever the wizard is closed.
  const close = () => (onDone ? onDone() : navigate("/pam/endpoint"));

  const clearSecrets = () => form.setFieldsValue({ password: undefined, privateKey: undefined });

  // Step 1's second half: hand the sudo account to the vault, which queues the
  // setup. The secret is dropped from the form as soon as the call returns.
  const saveCredential = (endpoint, values) => {
    const username = values.username.trim();
    setSaving(true);
    setCredError(null);
    setAdminCredential({
      instanceId: endpoint.instanceId,
      username,
      ...(values.method === "key" ? { privateKey: values.privateKey } : { password: values.password }),
      onSuccess: () => {
        clearSecrets();
        setSaving(false);
        setCreated({ ...endpoint, username });
        const description = String(values.description || "").trim();
        if (description && !descSaved) {
          saveDescription(endpoint.instanceId, description)
            .then(() => setDescSaved(true))
            .catch((err) =>
              messageApi.warning(
                `The description wasn't saved: ${err?.response?.data?.message || err?.response?.data?.error || "try again on the endpoint's page"}.`,
              ),
            );
        }
        setScreen(2);
      },
      onFailure: (err) => {
        clearSecrets();
        setSaving(false);
        setCredError(passwordErrorText(err, "Couldn't save the sudo account."));
      },
    });
  };

  const create = (values) => {
    setSaving(true);
    // Accounts are found by the scan after setup, so none are sent here.
    addInstance({
      instanceName: values.instanceName.trim(),
      privateIp: values.privateIp.trim(),
      jumpServerId: values.jumpServerId,
      onSuccess: (instanceId) => {
        if (!instanceId) {
          clearSecrets();
          setSaving(false);
          messageApi.error("The endpoint was added but its id wasn't returned. Find it on the endpoint list.");
          return;
        }
        const endpoint = {
          instanceId,
          name: values.instanceName.trim(),
          address: values.privateIp.trim(),
          jumpServerId: values.jumpServerId,
          gateway: gatewayOptions.find((o) => o.value === values.jumpServerId)?.label,
        };
        setCreated(endpoint);
        messageApi.success("Endpoint added");
        saveCredential(endpoint, values);
      },
      onFailure: (err) => {
        clearSecrets();
        setSaving(false);
        const serverMsg = err?.response?.data?.error || "";
        if (/no jump server/i.test(serverMsg)) {
          messageApi.error("Gateway no longer exists");
          form.setFieldValue("jumpServerId", undefined);
          loadGateways();
        } else if (err?.response?.status === 401) {
          messageApi.error("Credentials are invalid");
        } else {
          messageApi.error(serverMsg || "Couldn't add the endpoint");
        }
      },
    });
  };

  // Once the endpoint exists only the credential is sent again. Before that,
  // the backend does not check for duplicates, so warn here before saving.
  const submit = (values) => {
    if (created) return saveCredential(created, values);
    const address = values.privateIp.trim().toLowerCase();
    const dup = instances.find((r) => String(r.privateIp || "").trim().toLowerCase() === address);
    if (!dup) return create(values);
    modal.confirm({
      title: "An endpoint already uses this address",
      content: `${dup.hostName || dup.instanceName || `Endpoint #${dup.instanceId}`} already has ${values.privateIp.trim()}. Add another endpoint with the same address anyway?`,
      okText: "Add anyway",
      cancelText: "Cancel",
      onOk: () => create(values),
    });
  };

  // Read client-side only: the file never leaves the browser except inside
  // the adminCredential call.
  const readKeyFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > MAX_KEY_BYTES) {
      messageApi.error("That file is too big to be a private key.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      form.setFieldsValue({ privateKey: String(reader.result || "") });
      form.validateFields(["privateKey"]).catch(() => {});
    };
    reader.onerror = () => messageApi.error("Couldn't read the key file.");
    reader.readAsText(file);
  };

  // Back to the form for the same endpoint, credential only.
  const changeCredential = () => {
    setCredError(null);
    form.setFieldsValue({ username: created?.username || form.getFieldValue("username") });
    setScreen(1);
  };

  const gatewayOptions = (gateways || []).map((g) => ({
    value: g.id,
    label: g.lastSeenAt ? gatewayLabel(g) : `${gatewayLabel(g)} · never seen`,
  }));

  return (
    <OnboardShell
      embedded={embedded}
      eyebrow="PAM · Endpoints · Linux via SSH gateway"
      title="Add a Linux server (via SSH gateway)"
      subtitle="Nothing to install by hand. The gateway logs in with an existing sudo account and sets the machine up, then reaches it with a per-session key."
      onClose={close}
    >
      {msgCtx}
      {modalCtx}
      <Stepper current={screen} />

      {screen === 1 && (
        <div style={{ ...card, padding: 18 }}>
          <PartHead
            part="Step 1"
            title={created ? "Sudo account" : "Endpoint"}
            desc={
              created
                ? "The account Authnull uses to set up and manage this machine."
                : "The machine, the gateway that reaches it and an account Authnull can set it up with."
            }
          />
          <Form
            form={form}
            layout="vertical"
            requiredMark
            onFinish={submit}
            initialValues={{ method: "password" }}
            style={{ maxWidth: 560 }}
          >
            {created ? (
              // The endpoint is saved; only its credential is (re)sent from here.
              <div style={{ marginBottom: 16 }}>
                <Note tone="info">
                  <InlineValue>{created.name}</InlineValue> (<span style={{ fontFamily: AN_MONO }}>{created.address}</span>)
                  {created.gateway ? <> via {created.gateway}</> : null} is saved. Change its name or address on the
                  endpoint's page.
                </Note>
              </div>
            ) : (
              <>
                <Form.Item
                  name="instanceName"
                  label="Name"
                  extra="The label on the endpoint list."
                  rules={[{ required: true, whitespace: true, message: "Enter a name" }]}
                >
                  <Input placeholder="web-01.prod" />
                </Form.Item>

                <Form.Item
                  name="privateIp"
                  label="Address"
                  extra="The IP or host name the gateway connects to, usually its internal IP. Operators use the same address in their ssh command. No port — if sshd isn't on 22, write the policy host as address:port later."
                  rules={[
                    { required: true, whitespace: true, message: "Enter an IP or host name" },
                    {
                      validator: (_, v) => {
                        const t = String(v || "").trim();
                        if (!t) return Promise.resolve();
                        if (HOST_PORT_RE.test(t)) return Promise.reject(new Error("Leave the port off — address only"));
                        if (!ADDRESS_RE.test(t)) return Promise.reject(new Error("Enter an IP or host name, e.g. 10.0.0.4"));
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Input placeholder="10.0.0.4" style={{ fontFamily: AN_MONO }} />
                </Form.Item>

                <Form.Item
                  name="jumpServerId"
                  label="Gateway"
                  rules={[{ required: true, message: "Choose a gateway" }]}
                  extra={gateways && gateways.length === 0 ? "No gateways onboarded yet — onboard one from PAM → Gateways first." : undefined}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={gateways === null}
                    placeholder="Choose a gateway"
                    options={gatewayOptions}
                  />
                </Form.Item>
              </>
            )}

            <Form.Item
              name="username"
              label="Sudo username"
              extra="An existing account on the machine that can use sudo. Authnull stores its credential in the vault and uses it to set up and manage the machine."
              // The server's rule (adminCredential.js). The root-needs-a-key part
              // is reported under "Authenticate with" below.
              rules={[
                { required: true, whitespace: true, message: "Enter the account's username." },
                {
                  validator: (_, v) => {
                    const e = String(v || "").trim() ? adminUsernameError(v) : "";
                    return e ? Promise.reject(new Error(e)) : Promise.resolve();
                  },
                },
              ]}
            >
              <Input placeholder="ubuntu" autoComplete="off" spellCheck={false} style={{ fontFamily: AN_MONO }} />
            </Form.Item>

            <Form.Item
              name="method"
              label="Authenticate with"
              extra="root can only be used with a private key."
              dependencies={["username"]}
              rules={[
                ({ getFieldValue }) => ({
                  // Only the method-dependent part (root needs a key): a bad
                  // name is already reported on the username field.
                  validator: (_, m) => {
                    const u = getFieldValue("username");
                    const e = adminUsernameError(u) ? "" : adminUsernameError(u, m);
                    return e ? Promise.reject(new Error(e)) : Promise.resolve();
                  },
                }),
              ]}
            >
              <Segmented
                options={[
                  { label: "Password", value: "password" },
                  { label: "Private key", value: "key" },
                ]}
              />
            </Form.Item>

            {method === "key" ? (
              <Form.Item
                name="privateKey"
                label="Private key"
                extra="OpenSSH or PEM format, without a passphrase."
                rules={[
                  { required: true, whitespace: true, message: "Paste the private key or upload its file" },
                  { validator: (_, v) => (keyProblem(v) ? Promise.reject(new Error(keyProblem(v))) : Promise.resolve()) },
                ]}
              >
                <Input.TextArea
                  rows={6}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                  style={{ fontFamily: AN_MONO, fontSize: 12 }}
                />
              </Form.Item>
            ) : (
              <Form.Item name="password" label="Password" rules={[{ required: true, message: "Enter the account's password" }]}>
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            )}
            {method === "key" && (
              <div style={{ marginTop: -12, marginBottom: 16 }}>
                <input ref={keyFile} type="file" hidden onChange={readKeyFile} />
                <Button size="small" onClick={() => keyFile.current && keyFile.current.click()} icon={<Upload size={13} style={{ verticalAlign: -2 }} />}>
                  Upload key file
                </Button>
              </div>
            )}

            {!descSaved && (
              <Form.Item
                name="description"
                label="Description"
                rules={[{ max: 255, message: "Description must not exceed 255 characters" }]}
              >
                <Input.TextArea rows={2} placeholder="Optional" />
              </Form.Item>
            )}

            {credError && (
              <div style={{ marginBottom: 16 }}>
                <Note tone="danger" icon={<AlertTriangle size={15} />}>
                  {credError}
                </Note>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button onClick={close}>{created ? "Finish later" : "Cancel"}</Button>
              <Button type="primary" htmlType="submit" loading={saving}>
                {created ? "Save and set up" : "Add endpoint"}
              </Button>
            </div>
          </Form>
        </div>
      )}

      {screen === 2 && created && (
        <>
          <div style={{ ...card, padding: 18 }}>
            <PartHead
              part="Step 2"
              title="Set up the machine"
              desc="The gateway logs in with the sudo account and sets the machine up: Authnull's service account, the keys directory sshd reads and the helper it uses to manage local accounts."
            />
            <BootstrapStatus
              instanceId={created.instanceId}
              username={created.username}
              onDone={() => setScreen(3)}
              onChangeCredential={changeCredential}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "flex-start", gap: 8 }}>
            <Button onClick={close}>Finish later</Button>
          </div>
        </>
      )}

      {screen === 3 && created && (
        <>
          <div style={{ ...card, padding: 18 }}>
            <PartHead
              part="Step 3"
              title="Verify"
              desc="The machine is set up. The gateway now reads its accounts. You can close this — the scan carries on, and accounts are re-scanned daily."
            />
            <VerifyScan instanceId={created.instanceId} />
          </div>
          <Note tone="warn">With no matching policy, sessions to this host are refused.</Note>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <Button onClick={close}>Close</Button>
            <Button
              type="primary"
              onClick={() => {
                onDone && onDone();
                navigate(`/pam/policy/v2?${created.jumpServerId ? `gateway=${created.jumpServerId}&` : ""}host=${encodeURIComponent(created.address)}`);
              }}
            >
              Finish — write a policy <ArrowRight size={14} style={{ verticalAlign: -2, marginLeft: 6 }} />
            </Button>
          </div>
        </>
      )}
    </OnboardShell>
  );
}

// Step 2's body: reads the setup's status (backing off, see setupPollDelay) while mounted
// and calls onDone once it has finished. A failed setup offers Retry (the same
// credential) or Change credential; after SETUP_TIMEOUT_MS it stops asking.
function BootstrapStatus({ instanceId, username, onDone, onChangeCredential }) {
  const [status, setStatus] = useState(null); // adminCredentialStatus's response
  // waiting | failed | timeout
  const [phase, setPhase] = useState("waiting");
  // Bumped to start a fresh round of polling (and a fresh timeout).
  const [round, setRound] = useState(0);
  const [pollError, setPollError] = useState(null);
  const [rerun, setRerun] = useState({ busy: false, error: null });
  const done = useRef(onDone);
  done.current = onDone;

  useEffect(() => {
    if (phase !== "waiting") return undefined;
    // `alive` also drops answers to requests sent before a Retry.
    let alive = true;
    const started = Date.now();
    const tick = () => {
      if (Date.now() - started >= SETUP_TIMEOUT_MS) {
        setPhase("timeout");
        return;
      }
      getAdminCredentialStatus({
        instanceId,
        onSuccess: (res) => {
          if (!alive) return;
          setPollError(null);
          setStatus(res);
          const s = res?.bootstrap?.status;
          if (s === "done") {
            alive = false;
            done.current();
          } else if (s === "failed" || s === "expired") {
            setPhase("failed");
          }
        },
        onFailure: (err) => alive && setPollError(passwordErrorText(err, "Couldn't read the setup status.")),
      });
    };
    let t = null;
    const loop = () => {
      tick();
      if (alive) t = setTimeout(loop, setupPollDelay(Date.now() - started));
    };
    loop();
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [phase, round, instanceId]);

  const resume = () => {
    setStatus(null);
    setPollError(null);
    setPhase("waiting");
    setRound((r) => r + 1);
  };

  const retry = () => {
    setRerun({ busy: true, error: null });
    rerunAdminBootstrap({
      instanceId,
      onSuccess: () => {
        setRerun({ busy: false, error: null });
        resume();
      },
      onFailure: (err) => setRerun({ busy: false, error: passwordErrorText(err, "Couldn't start the setup again.") }),
    });
  };

  const line = { display: "flex", alignItems: "center", gap: 10, fontSize: 13, fontFamily: AN_FONT, color: AN_COLORS.ink2 };
  const hint = { fontSize: 12, color: AN_COLORS.muted, fontFamily: AN_FONT, lineHeight: 1.5 };
  const user = username || status?.username;

  if (phase === "failed") {
    const b = status?.bootstrap || {};
    return (
      <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
        <Note tone="danger" icon={<AlertTriangle size={15} />}>
          <strong>The setup {b.status === "expired" ? "expired" : "failed"}</strong>
          {b.error ? (
            <>
              : <span style={{ fontFamily: AN_MONO, fontSize: 12 }}>{b.error}</span>
            </>
          ) : (
            "."
          )}{" "}
          Check that {user ? <InlineValue>{user}</InlineValue> : "the account"} exists on the machine, that its password
          or key is right and that it can use sudo.
        </Note>
        {rerun.error && (
          <Note tone="danger" icon={<AlertTriangle size={15} />}>
            {rerun.error}
          </Note>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button onClick={retry} loading={rerun.busy} icon={<RefreshCw size={13} style={{ verticalAlign: -2 }} />}>
            Retry
          </Button>
          <Button onClick={onChangeCredential} disabled={rerun.busy}>
            Change credential
          </Button>
        </div>
      </div>
    );
  }

  if (phase === "timeout") {
    return (
      <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
        <Note tone="warn" icon={<AlertTriangle size={15} />}>
          The setup still isn't done after 10 minutes. The gateway may be offline or unable to reach the machine — it
          keeps trying for up to an hour.
        </Note>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button onClick={resume} icon={<RefreshCw size={13} style={{ verticalAlign: -2 }} />}>
            Retry
          </Button>
          <Button onClick={onChangeCredential}>Change credential</Button>
        </div>
      </div>
    );
  }

  const claimed = status?.bootstrap?.status === "claimed";
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={line}>
        <Spin size="small" />
        Setting up the machine…
      </div>
      <div style={hint}>
        {claimed ? (
          <>The gateway has logged in{user ? <> as <InlineValue>{user}</InlineValue></> : null} and is running the setup.</>
        ) : (
          "Waiting for the gateway to pick the setup up. This usually takes under a minute."
        )}
      </div>
      {pollError && <div style={{ ...hint, color: AN_COLORS.danger }}>{pollError}</div>}
    </div>
  );
}

// Step 3's body: polls the first scan while mounted, then lists what it found.
function VerifyScan({ instanceId }) {
  const { status } = useScanStatus(instanceId, { poll: true });
  const [accounts, setAccounts] = useState(null);
  const done = status?.status === "done";

  useEffect(() => {
    if (!done) return;
    listEndpointAccounts({
      instanceIds: [instanceId],
      onSuccess: (rows) => setAccounts(rows[0]?.accounts || []),
      onFailure: () => setAccounts([]),
    });
  }, [done, instanceId]);

  return (
    <ScanStatusBody
      status={status}
      accounts={done ? accounts || undefined : undefined}
      neverText="No scan has been queued yet. It starts once setup has finished — check the endpoint's page in a minute."
      waitingText="Scanning the machine's accounts…"
      waitingHint="The first scan runs right after setup and usually takes under a minute."
      failedHint={
        <>
          Press <strong>Scan accounts</strong> on the endpoint to try again.
        </>
      }
    />
  );
}

export default OnboardSshTarget;
